import { Component } from "react";
import { Routes, Route, Navigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { AdminProvider } from "./auth/AdminContext";
import { msalInstance } from "./auth/msalInstance";
import Nav from "./components/Nav";
import LoginScreen from "./components/LoginScreen";
import EstimatingAssistant from "./pages/EstimatingAssistant";
import DuctEstimator from "./pages/DuctEstimator";
import Estimates from "./pages/Estimates";
import EstimateDetail from "./pages/EstimateDetail";
import Turnovers from "./pages/Turnovers";
import TurnoverDetail from "./pages/TurnoverDetail";
import Integrations from "./pages/Integrations";
import Help from "./pages/Help";
import BODItemLibrary from "./pages/BODItemLibrary";
import ReferencePricingLibrary from "./pages/ReferencePricingLibrary";
import SystemPromptsAdmin from "./pages/SystemPromptsAdmin";
import TurnoverConfig from "./pages/TurnoverConfig";
import TurnoverPackage from "./pages/TurnoverPackage";
import QuoteLogSetup from "./pages/QuoteLogSetup";
import EstimateCostLibrary from "./pages/EstimateCostLibrary";
import Vendors from "./pages/Vendors";
import CostCodes from "./pages/CostCodes";
import ConstructionStandards from "./pages/ConstructionStandards";
import Learning from "./pages/Learning";
import BasisOfDesign from "./pages/BasisOfDesign";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";

// Emails sent before 2026-09-23 linked to /turnovers/:id (plural), which isn't
// a route — the catch-all sent every "Open in EstAssist" click to the home
// page. Keep old links working; the query string (?section=N) carries over.
function LegacyTurnoverLink() {
  const { turnoverID } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/turnover/${turnoverID}${search}`} replace />;
}

class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: "monospace", fontSize: 13, color: "#dc2626" }}>
          <strong>Page error:</strong> {this.state.error.message}
          <pre style={{ marginTop: 12, fontSize: 11, whiteSpace: "pre-wrap", color: "#374151" }}>
            {this.state.error.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 12, padding: "6px 14px" }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  // The Room Takeoff Layout Canvas always opens in its own dedicated window
  // (see RoomTakeoff.jsx's openCanvasWindow) sized for a second monitor —
  // that window's ?view=canvas page load skips the app's own Nav bar
  // entirely (EstimateDetail.jsx does the same for its sidebar/breadcrumb),
  // so the popped-out window shows nothing but the canvas itself.
  const [searchParams] = useSearchParams();
  const canvasOnly = searchParams.get("view") === "canvas";
  return (
    <MsalProvider instance={msalInstance}>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
       <AdminProvider>
        {!canvasOnly && <Nav kbStatus="connected" />}
        {/* Every route wrapped individually (not once around <Routes>) so an
            error boundary that's already tripped resets on the NEXT
            navigation instead of staying blank/stuck on the page that broke
            — PageErrorBoundary has no componentDidUpdate/key-based reset, and
            React Router swaps in a new element instance per path already, so
            per-route wrapping gets that reset for free. Previously only
            /turnover/:turnoverID had this; every other route had no error
            boundary anywhere above it, so an uncaught render error unmounted
            the ENTIRE app (nav bar included) instead of just that page. */}
        <Routes>
          <Route path="/"                      element={<PageErrorBoundary><EstimatingAssistant /></PageErrorBoundary>} />
          <Route path="/duct-estimator"        element={<PageErrorBoundary><DuctEstimator /></PageErrorBoundary>} />
          <Route path="/estimates"              element={<PageErrorBoundary><Estimates /></PageErrorBoundary>} />
          <Route path="/estimates/:projectId"   element={<PageErrorBoundary><EstimateDetail /></PageErrorBoundary>} />
          <Route path="/turnover"              element={<PageErrorBoundary><Turnovers /></PageErrorBoundary>} />
          <Route path="/turnover/:turnoverID/package" element={<PageErrorBoundary><TurnoverPackage /></PageErrorBoundary>} />
          <Route path="/turnover/:turnoverID"  element={<PageErrorBoundary><TurnoverDetail /></PageErrorBoundary>} />
          <Route path="/turnovers/:turnoverID" element={<LegacyTurnoverLink />} />
          <Route path="/integrations"              element={<PageErrorBoundary><Integrations /></PageErrorBoundary>} />
          <Route path="/integrations/qbo-connect"  element={<PageErrorBoundary><Integrations /></PageErrorBoundary>} />
          <Route path="/integrations/disconnected" element={<PageErrorBoundary><Integrations /></PageErrorBoundary>} />
          <Route path="/bod-items"             element={<PageErrorBoundary><BODItemLibrary /></PageErrorBoundary>} />
          <Route path="/reference-pricing"     element={<PageErrorBoundary><ReferencePricingLibrary /></PageErrorBoundary>} />
          <Route path="/system-prompts"        element={<PageErrorBoundary><SystemPromptsAdmin /></PageErrorBoundary>} />
          <Route path="/turnover-config"       element={<PageErrorBoundary><TurnoverConfig /></PageErrorBoundary>} />
          <Route path="/quote-log-setup"       element={<PageErrorBoundary><QuoteLogSetup /></PageErrorBoundary>} />
          <Route path="/estimate-engine"       element={<PageErrorBoundary><EstimateCostLibrary /></PageErrorBoundary>} />
          {/* Pre-2026-09-11 path for the same page (was "Estimate Cost Library") — kept so old bookmarks keep working */}
          <Route path="/estimate-cost-library" element={<Navigate to="/estimate-engine" replace />} />
          <Route path="/vendors"               element={<PageErrorBoundary><Vendors /></PageErrorBoundary>} />
          <Route path="/cost-codes"            element={<PageErrorBoundary><CostCodes /></PageErrorBoundary>} />
          <Route path="/construction-standards" element={<PageErrorBoundary><ConstructionStandards /></PageErrorBoundary>} />
          <Route path="/learning"               element={<PageErrorBoundary><Learning /></PageErrorBoundary>} />
          <Route path="/basis-of-design"        element={<PageErrorBoundary><BasisOfDesign /></PageErrorBoundary>} />
          <Route path="/development-roadmap"    element={<Navigate to="/" replace />} />{/* retired 2026-10-22 — the roadmap lives in GitHub; old email links land home */}
          <Route path="/executive-dashboard"   element={<PageErrorBoundary><ExecutiveDashboard /></PageErrorBoundary>} />
          <Route path="/help/feedback"         element={<PageErrorBoundary><Help /></PageErrorBoundary>} />
          <Route path="/help/inbox"            element={<PageErrorBoundary><Help /></PageErrorBoundary>} />
          <Route path="/help"                  element={<Navigate to="/help/feedback" replace />} />
          <Route path="*"                      element={<Navigate to="/" replace />} />
        </Routes>
       </AdminProvider>
      </AuthenticatedTemplate>
    </MsalProvider>
  );
}
